import { SgTimepicker } from '@sg-bootstrap/components/dist/react-library/src/components';
import { isDefined, isNotDefined } from '@sgme/fp';
import { Checkbox } from '@sgme/ui';
import { useCallback, useContext } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import {
  StopLossContext,
  useStopLossSetIsModifyingField,
  useValidateStopLoss,
} from '@/App/tiles/orders/StopLossOrder/StopLossOrderCreationTile/stopLossContext';
import { SgDatePicker } from '@/components/sg-design-system/components/input/SgDatePicker/index.tsx';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { selectExpiryStopLoss, selectStopLossErrors } from '@/store/orders/stopLoss/selectors.ts';
import { getUserPreferences } from '@/store/userPreferences/slice.ts';
import { DATE_FORMAT_TEXT } from '@/utils/date.ts';

export const StopLossExpiry = () => {
  const tileId = useContext(StopLossContext).tileId;
  const setIsModifyingField = useStopLossSetIsModifyingField();
  const track = useTrackEvent();

  const { formatMessage, formatTime, locale } = useIntl();
  const { dateInputCultureInfo } = useAppSelector(getUserPreferences);

  const expiry = useAppSelector((state) => selectExpiryStopLoss(state, tileId));
  const errors = useAppSelector((state) => selectStopLossErrors(state, tileId));

  const validateStopLoss = useValidateStopLoss();

  const handleIsGoodTillCancelledChange = (isGoodTillConcelled: boolean) => {
    track('Order action', 'Good till cancelled changed');
    validateStopLoss({
      expiry: isGoodTillConcelled
        ? { type: 'GoodTillCancelled' }
        : { type: 'ExpiryDate', date: undefined, time: undefined },
    });
  };

  const handleDateChange = (date: string) => {
    track('Order action', 'expiry date changed');
    if (isNotDefined(expiry) || expiry.type === 'GoodTillCancelled') {
      return;
    }

    const newExpiry = {
      type: 'ExpiryDate' as const,
      date,
      time: expiry.time, // Keep the existing time if it exists
    };

    validateStopLoss({
      expiry: newExpiry,
    });
  };

  const expiryDateError = errors?.expiryDay;
  const expiryTimeError = errors?.expiryTime;

  const handleTimeChange = useCallback(
    ({
      hour = 0,
      minute = 0,
      second = 0,
      period,
    }: {
      hour?: number;
      minute?: number;
      second?: number;
      period?: 'AM' | 'PM';
    }) => {
      track('Order action', 'expiry time changed');
      if (isNotDefined(expiry) || expiry.type === 'GoodTillCancelled') {
        return;
      }

      const hour24 = period === 'PM' && hour < 12 ? hour + 12 : hour;

      const newExpiry = {
        type: 'ExpiryDate' as const,
        date: expiry.date, // Keep the existing date from ref
        time: formatTime(new Date(0, 0, 0, hour24, minute, second), { timeStyle: 'medium', hour12: false }),
      };

      validateStopLoss({
        expiry: newExpiry,
      });
    },
    [expiry, validateStopLoss, formatTime],
  );

  const getComputedLocale = () => {
    if (dateInputCultureInfo === 'en') {
      return 'en-US';
    }
    if (dateInputCultureInfo === 'fr') {
      return 'fr-FR';
    }
    if (locale === 'en') {
      return 'en-GB';
    }
    return 'fr-FR';
  };

  return (
    <OrderRow labelId="order.fields.expiry.label" alignTop>
      <Checkbox
        dataE2e="GTC-checkbox"
        checked={expiry?.type === 'GoodTillCancelled'}
        onCheckBoxChange={handleIsGoodTillCancelledChange}
        label={formatMessage({ id: 'order.fields.expiry.goodTillCanceled' })}
        size="sm"
      />

      {expiry?.type === 'ExpiryDate' && (
        <div
          className="d-flex flex-column flex-nowrap  gap-4px align-items-between justify-content-between flex-grow-0 flex-shrink-0"
          data-e2e="expiry-date"
        >
          <OmsFieldErrorWrapper errors={expiryDateError}>
            <SgDatePicker
              size="sm"
              yearControls
              value={expiry.date}
              placeholder="Expiry date"
              locale={locale === 'en' ? 'en-GB' : 'fr-FR'}
              dateInputCultureInfo={getComputedLocale()}
              onChange={handleDateChange}
              onFocus={() => setIsModifyingField(true)}
              onBlurInput={() => setIsModifyingField(false)}
              showClearButton={false}
              validationState={isDefined(expiryDateError) ? 'invalid' : undefined}
              dateFormat={DATE_FORMAT_TEXT}
            />
          </OmsFieldErrorWrapper>

          <OmsFieldErrorWrapper errors={expiryTimeError}>
            <SgTimepicker
              size="sm"
              appendTo="body"
              withSeconds
              value={isDefined(expiry.time) ? expiry.time : undefined}
              onTimeSelected={(e) => handleTimeChange(e.detail)}
              state={isDefined(expiryTimeError) ? 'invalid' : undefined}
            />
          </OmsFieldErrorWrapper>
        </div>
      )}
    </OrderRow>
  );
};
